import { Order, Owner, OwnerType } from "@/api/SolarCloudApi";
import { useEffect } from "react";
import { LockIcon } from "lucide-react";
import { OwnerChoices } from "./OrganisationChoices";
import UserForm from "./UserForm";
import { useBuyPanelsStore } from "../store";
import OrganisationForm from "./OrganisationForm";
import { useQueryClient } from "@tanstack/react-query";
import { useCompleteStep } from "../hook";
import { useOrderUuid } from "@/stores/orderIdStore";
import { useUpdateNewOrder } from "@/queries/order/useUpdateNewOrder";
import { useNewOrder } from "@/queries/order/useNewOrder";
import { useProfileContext } from "@/contexts/ProfileContext";
import AuthProcess from "../../auth/AuthProcess";
import { useUser } from "@/queries/user/useUser";
import { useState } from "react";
import LoadingSection from "@/components/LoadingSection";
import Payment from "@/panel/buypanels/payments/Payment";
import { toast } from "sonner";
import { useLocation } from "react-router";
import { isCorporateSolarPath } from "../route";

export default function Ownership() {
  const location = useLocation();
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);
  const orderUuid = useOrderUuid();
  const profile = useProfileContext();
  const { data: currentUser, refetch } = useUser();
  const { mutate: updateOrder, mutateAsync: updateOrderAsync } =
    useUpdateNewOrder();
  const { data: order, isLoading } = useNewOrder({
    uuid: orderUuid,
  });
  const queryClient = useQueryClient();
  const ownerType = useBuyPanelsStore((state) => state.data.ownerType);
  const setOwnerType = useBuyPanelsStore((state) => state.setOwnerType);
  const getPreviousStep = useBuyPanelsStore((state) => state.getPreviousStep);
  const completeStep = useCompleteStep();

  const isAuthenticated = profile.authentication?.loggedIn;

  useEffect(() => {
    if (!ownerType) {
      const defaultType = isCorporateSolarPath(location.pathname)
        ? OwnerType.ORGANISATION
        : OwnerType.USER;
      setOwnerType(defaultType);
    }
  }, [location.pathname, ownerType, setOwnerType]);

  // Auto-upsert owner on mount when order has no owner but the user profile exists
  useEffect(() => {
    let cancelled = false;
    const attachOwnerIfMissing = async () => {
      if (!order || order.owner || isCorporateSolarPath(location.pathname) || ownerType === OwnerType.ORGANISATION) return;
      setIsUpdatingOrder(true);

      try {
        const { data: user } = await refetch();
        if (!user || !user.uuid) {
          if (!cancelled) setIsUpdatingOrder(false);
          return;
        }

        const owner: Owner = {
          type: OwnerType.USER,
          user,
        };

        const orderWithOwner: Order = {
          ...order,
          owner,
        };

        updateOrder(orderWithOwner, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["authentication"] });
            if (!cancelled) setIsUpdatingOrder(false);
          },
          onError: (err) => {
            console.error("Auto-upsert owner failed", err);
            if (!cancelled) setIsUpdatingOrder(false);
          },
        });
      } catch (e) {
        console.error("Auto-upsert owner error", e);
        if (!cancelled) setIsUpdatingOrder(false);
      }
    };

    attachOwnerIfMissing();

    return () => {
      cancelled = true;
    };
  }, [order, refetch, updateOrder, queryClient]);

  const handleBack = () => {
    window.location.href = getPreviousStep();
  };

  const onVerified = async () => {
    setIsUpdatingOrder(true);

    const { data: user } = await refetch();

    if (!user || !user.uuid) {
      toast.error(
        "User not found after sign-up. Please refresh and try again."
      );
      setIsUpdatingOrder(false);
      return;
    }

    if (!order?.owner) {
      const owner: Owner = {
        type: OwnerType.USER,
        user,
      };

      const orderWithOwner: Order = {
        ...order,
        owner,
      };
      updateOrder(orderWithOwner, {
        onSuccess: () => {
          setIsUpdatingOrder(false);
          queryClient.invalidateQueries({ queryKey: ["authentication"] });
        },
        onError: (error) => {
          console.error("Error updating order:", error);
          toast.error("Failed to update order. Please try again.");
          setIsUpdatingOrder(false);
        },
      });
    } else {
      setIsUpdatingOrder(false);
    }
  };

  const onSuccess = async (uuid: string) => {
    queryClient.invalidateQueries({ queryKey: ["authentication"] });
    if (ownerType === OwnerType.ORGANISATION) {
      const { data: refreshedUser } = await refetch();
      const ownerUser = refreshedUser ?? currentUser;
      if (!ownerUser?.uuid) {
        throw new Error("The authenticated user could not be loaded.");
      }

      await updateOrderAsync(
        {
          ...order,
          owner: {
            type: OwnerType.ORGANISATION,
            organisation: {
              uuid,
            },
            // PayPal initialization requires the authenticated user to remain
            // attached even when the purchase is owned by an organisation.
            user: {
              uuid: ownerUser.uuid,
            },
          },
        },
      );
      queryClient.invalidateQueries({ queryKey: ["authentication"] });
      toast.success("Organisation details saved.");
    } else if (ownerType === OwnerType.USER) {
      await updateOrderAsync(
        {
          ...order,
          owner: {
            type: OwnerType.USER,
            user: {
              uuid,
            },
          },
        },
      );
      queryClient.invalidateQueries({ queryKey: ["authentication"] });
    }
  };

  if (isLoading) return <LoadingSection />;

  // Show signup form if user is not authenticated
  if (!isAuthenticated) {
    return <AuthProcess onVerified={onVerified} handleBack={handleBack} />;
  }

  // Show ownership form if user is authenticated
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Left Column: Essential Actions & Info */}
        <div className="lg:col-span-6 space-y-8">
          <section className="bg-card rounded-2xl p-6 border shadow-sm">
            <h1 className="text-xl font-bold mb-6 flex items-center gap-3">
              Ownership
            </h1>
            <div className="space-y-8">
              <OwnerChoices ownerType={ownerType} setOwnerType={setOwnerType} />

              <div className="pt-4 border-t border-dashed">
                {ownerType === OwnerType.ORGANISATION && (
                  <OrganisationForm onSuccess={onSuccess} />
                )}
                {ownerType === OwnerType.USER && (
                  <UserForm onSuccess={onSuccess} />
                )}
              </div>
            </div>
          </section>
        </div>

        {/* Right Column: Checkout Experience */}
        <div className="lg:col-span-6">
          <section className="bg-card rounded-2xl p-6 border shadow-sm sticky top-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Pay by</h2>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <LockIcon className="h-3.5 w-3.5" />
                <span>All transactions are secure and encrypted</span>
              </div>
            </div>
            <div className="space-y-6">
              <Payment
                disableRedirect
                fullWidth
                allowPayment
                requiresPaymentDetails
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
